import React from 'react';
import {
  Users,
  BarChart3,
  CheckCircle,
  Server,
} from 'lucide-react';
import FrameworkPerformanceChart from '../../../../components/charts/FrameworkPerformanceChart';
import ComplianceTrendChart from '../../../../components/charts/ComplianceTrendChart';
import EmissionsChart from '../../../../components/charts/EmissionsChart';
import UserEngagementChart from '../../../../components/charts/UserEngagementChart';
import SystemPerformanceChart from '../../../../components/charts/SystemPerformanceChart';
import RiskAssessmentChart from '../../../../components/charts/RiskAssessmentChart';
import ComplianceDistributionChart from '../../../../components/charts/ComplianceDistributionChart';
import GanttChart from '../../../../components/charts/GanttChart';
import { AnalyticsTabProps } from './types';

const AnalyticsTab: React.FC<AnalyticsTabProps> = ({
  selectedTimeRange,
  onTimeRangeChange,
  platformMetrics,
  frameworkPerformanceData,
  emissionsData,
  userEngagementData,
  systemPerformanceData,
  platformRiskData,
  complianceDistributionData,
  ganttTasks,
  analytics,
}) => {
  return (
    <div className="space-y-6">
      {/* Analytics Header */}
      <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="text-blue-600" size={16} />
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Enterprise Analytics Dashboard</h3>
              <p className="text-xs text-slate-500">Real-time platform insights and performance metrics</p>
            </div>
          </div>
          <div className="flex gap-2">
            {(['7d', '30d', '90d'] as const).map((range) => (
              <button
                key={range}
                onClick={() => onTimeRangeChange(range)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  selectedTimeRange === range
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {range}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Analytics Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Framework Performance - Full Width */}
        {frameworkPerformanceData.length > 0 && (
          <div className="lg:col-span-2">
            <FrameworkPerformanceChart
              data={frameworkPerformanceData}
              title="Framework Performance Across Platform"
              height={280}
            />
          </div>
        )}

        {/* Compliance Trend */}
        {platformMetrics.complianceTrend.labels?.length > 0 && (
          <ComplianceTrendChart
            data={platformMetrics.complianceTrend}
            title="Platform Compliance Trends"
            height={280}
          />
        )}

        {/* Emissions Chart */}
        {emissionsData.labels.length > 0 && (
          <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <h4 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-green-600" />
              GHG Emissions Tracking
            </h4>
            <EmissionsChart
              data={emissionsData}
              title=""
              height={280}
            />
          </div>
        )}

        {/* User Engagement Chart */}
        {userEngagementData.labels.length > 0 && (
          <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <h4 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-600" />
              User Engagement Metrics
            </h4>
            <UserEngagementChart
              data={userEngagementData}
              title=""
              height={280}
            />
          </div>
        )}

        {/* System Performance Chart */}
        {systemPerformanceData.labels.length > 0 && (
          <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <h4 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Server className="w-4 h-4 text-sky-700" />
              System Performance Trends
            </h4>
            <SystemPerformanceChart
              data={systemPerformanceData}
              title=""
              height={280}
            />
          </div>
        )}

        {/* Risk Assessment */}
        {platformRiskData.length > 0 && (
          <RiskAssessmentChart
            data={platformRiskData}
            title="Platform Risk Assessment"
            height={280}
          />
        )}

        {/* Compliance Distribution */}
        {complianceDistributionData.labels.length > 0 && (
          <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <h4 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
              Compliance Distribution
            </h4>
            <ComplianceDistributionChart
              data={complianceDistributionData}
              title=""
              height={280}
            />
          </div>
        )}

        {/* Gantt Chart - Full Width */}
        {ganttTasks.length > 0 && (
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <h4 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-purple-600" />
                Project Timeline
              </h4>
              <GanttChart
                tasks={ganttTasks}
                title=""
                height={280}
              />
            </div>
          </div>
        )}
      </div>

      {/* Platform Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider text-left">Response Time</p>
          <p className="text-xl font-bold text-slate-900 mt-1">{analytics.avgResponseTime !== null ? analytics.avgResponseTime + 'ms' : '\u2014'}</p>
        </div>
        <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider text-left">Error Rate</p>
          <p className="text-xl font-bold text-slate-900 mt-1">{analytics.errorRate !== null ? analytics.errorRate.toFixed(2) + '%' : '\u2014'}</p>
        </div>
        <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider text-left">Active Companies</p>
          <p className="text-xl font-bold text-slate-900 mt-1">{analytics.activeCompanies} <span className="text-xs font-normal text-slate-400">/ {analytics.totalCompanies}</span></p>
        </div>
        <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider text-left">Expiring Soon</p>
          <p className={`text-xl font-bold mt-1 ${analytics.expiringCompanies > 0 ? 'text-red-600' : 'text-slate-900'}`}>{analytics.expiringCompanies}</p>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsTab;
